import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import {
  passwordChangeSchema,
  computePasswordStrength,
  type PasswordChangeFormValues,
} from '../schema/myProfileSchema';
import { useChangePassword } from '../hooks/useChangePassword';
import type { ApiError } from '../types/myProfile';

interface PasswordChangeTabProps {
  onSuccess?: (msg: string) => void;
}

const STRENGTH_COLOR: Record<number, string> = {
  0: 'var(--color-surface-border)',
  1: 'var(--color-danger)',
  2: 'var(--color-warning)',
  3: 'var(--color-info)',
  4: 'var(--color-success)',
};

function StrengthBar({ strength }: { strength: 0 | 1 | 2 | 3 | 4 }) {
  const { t } = useTranslation();
  const strengthKey = String(strength) as '0' | '1' | '2' | '3' | '4';
  const label = t(`myProfile.password.strength.${strengthKey}`, { defaultValue: '' });
  return (
    <div
      aria-label={`${t('myProfile.password.strengthLabel')}: ${label || t('myProfile.password.strengthNone')}`}
      style={{ marginTop: 'var(--spacing-1)' }}
    >
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        {[1, 2, 3, 4].map((level) => (
          <div
            key={level}
            style={{
              flex: 1,
              height: 4,
              borderRadius: 'var(--radius-full)',
              background: strength >= level ? STRENGTH_COLOR[strength] : 'var(--color-surface-border)',
              transition: 'background 0.3s',
            }}
          />
        ))}
      </div>
      {strength > 0 && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: STRENGTH_COLOR[strength],
            fontWeight: 600,
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}

function PasswordCriteria({ password }: { password: string }) {
  const { t } = useTranslation();
  const checks = [
    { labelKey: 'myProfile.password.criteria.minLength', met: password.length >= 10 },
    { labelKey: 'myProfile.password.criteria.upper', met: /[A-Z]/.test(password) },
    { labelKey: 'myProfile.password.criteria.lower', met: /[a-z]/.test(password) },
    { labelKey: 'myProfile.password.criteria.digit', met: /[0-9]/.test(password) },
    { labelKey: 'myProfile.password.criteria.special', met: /[^A-Za-z0-9]/.test(password) },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--spacing-1)',
        marginTop: 'var(--spacing-2)',
      }}
    >
      {checks.map((c) => (
        <span
          key={c.labelKey}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            fontSize: 'var(--font-size-xs)',
            color: c.met ? 'var(--color-success)' : 'var(--color-text-muted)',
            padding: '1px var(--spacing-2)',
            borderRadius: 'var(--radius-full)',
            background: c.met
              ? 'color-mix(in srgb, var(--color-success) 12%, transparent)'
              : 'var(--color-surface-elevated)',
            border: `1px solid ${c.met ? 'var(--color-success)' : 'var(--color-surface-border)'}`,
          }}
        >
          {c.met ? '✓' : '○'} {t(c.labelKey)}
        </span>
      ))}
    </div>
  );
}

const inputBase: React.CSSProperties = {
  flex: 1,
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  outline: 'none',
  minWidth: 0,
};

const inputErrorBase: React.CSSProperties = {
  ...inputBase,
  borderColor: 'var(--color-danger)',
};

export function PasswordChangeTab({ onSuccess }: PasswordChangeTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mutation = useChangePassword();

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PasswordChangeFormValues>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPasswordValue = watch('newPassword');
  const strength = computePasswordStrength(newPasswordValue);

  const onSubmit = handleSubmit((values) => {
    setServerError(undefined);
    mutation.mutate(
      {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      },
      {
        onSuccess: () => {
          onSuccess?.(t('myProfile.password.toast.relogin'));
          // Clear stored tokens since server revoked all sessions
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          sessionStorage.removeItem('access_token');
          sessionStorage.removeItem('refresh_token');
          void navigate('/login');
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          if (apiErr.code === 'PROFILE-403-001') {
            // R9-C-006: wrong currentPassword → always show Korean message regardless of HTTP status
            setServerError(t('myProfile.error.currentPasswordMismatch'));
          } else if (apiErr.code === 'PROFILE-409-001') {
            setServerError(t('myProfile.error.passwordReuse'));
          } else if (apiErr.code === 'PROFILE-422-001') {
            setServerError(t('myProfile.error.validationFailed'));
          } else if (apiErr.code === 'PROFILE-422-002') {
            // R7-063: BE confirmPassword equality check
            setServerError(t('myProfile.error.passwordConfirmationMismatch'));
          } else if (apiErr.code?.startsWith('HTTP-4') || apiErr.code?.startsWith('HTTP-5')) {
            // Raw HTTP error (e.g. WAF/gateway 400) — show generic Korean message
            setServerError(t('myProfile.password.changeFailedMsg'));
          } else {
            setServerError(apiErr.message ?? t('myProfile.password.changeFailedMsg'));
          }
        },
      }
    );
  });

  const eyeButtonStyle: React.CSSProperties = {
    padding: 'var(--spacing-2)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-xs)',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
  };

  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnGhost: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'transparent',
    color: 'var(--color-text-secondary)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <h2
        style={{
          margin: 0,
          marginBottom: 'var(--spacing-6)',
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
        }}
      >
        {t('myProfile.password.title')}
      </h2>

      {/* Policy banner */}
      <div
        style={{
          padding: 'var(--spacing-3) var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
          background: 'color-mix(in srgb, var(--color-info) 8%, var(--color-surface-muted))',
          border: '1px solid color-mix(in srgb, var(--color-info) 30%, transparent)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {t('myProfile.password.policy')}
      </div>

      {serverError && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            marginBottom: 'var(--spacing-4)',
            background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-danger)',
          }}
        >
          {serverError}
        </div>
      )}

      <form
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        aria-label={t('myProfile.password.formAriaLabel')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          {/* Current password */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
            <label
              htmlFor="currentPassword"
              style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}
            >
              {t('myProfile.password.currentPassword.label')}
            </label>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <input
                id="currentPassword"
                type={showCurrent ? 'text' : 'password'}
                placeholder={t('myProfile.password.currentPassword.placeholder')}
                autoComplete="current-password"
                aria-invalid={!!errors.currentPassword}
                aria-describedby={errors.currentPassword ? 'currentPassword-error' : undefined}
                {...register('currentPassword')}
                style={errors.currentPassword ? inputErrorBase : inputBase}
              />
              <button
                type="button"
                aria-label={showCurrent ? t('myProfile.password.hidePassword') : t('myProfile.password.showPassword')}
                onClick={() => setShowCurrent((v) => !v)}
                style={eyeButtonStyle}
                tabIndex={0}
              >
                {showCurrent ? t('myProfile.password.hideShort') : t('myProfile.password.showShort')}
              </button>
            </div>
            {errors.currentPassword && (
              <span
                id="currentPassword-error"
                role="alert"
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
              >
                {errors.currentPassword.message}
              </span>
            )}
          </div>

          {/* New password */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
            <label
              htmlFor="newPassword"
              style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}
            >
              {t('myProfile.password.newPassword.label')}
            </label>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <input
                id="newPassword"
                type={showNew ? 'text' : 'password'}
                placeholder={t('myProfile.password.newPassword.placeholder')}
                autoComplete="new-password"
                aria-invalid={!!errors.newPassword}
                aria-describedby={[
                  errors.newPassword ? 'newPassword-error' : '',
                  'newPassword-strength',
                ]
                  .filter(Boolean)
                  .join(' ') || undefined}
                {...register('newPassword')}
                style={errors.newPassword ? inputErrorBase : inputBase}
              />
              <button
                type="button"
                aria-label={showNew ? t('myProfile.password.hidePassword') : t('myProfile.password.showPassword')}
                onClick={() => setShowNew((v) => !v)}
                style={eyeButtonStyle}
                tabIndex={0}
              >
                {showNew ? t('myProfile.password.hideShort') : t('myProfile.password.showShort')}
              </button>
            </div>
            {/* Strength bar */}
            <div id="newPassword-strength">
              <StrengthBar strength={strength} />
            </div>
            {/* Criteria checklist */}
            {newPasswordValue && <PasswordCriteria password={newPasswordValue} />}
            {errors.newPassword && (
              <span
                id="newPassword-error"
                role="alert"
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
              >
                {errors.newPassword.message}
              </span>
            )}
          </div>

          {/* Confirm password */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
            <label
              htmlFor="confirmPassword"
              style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}
            >
              {t('myProfile.password.confirmPassword.label')}
            </label>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                placeholder={t('myProfile.password.confirmPassword.placeholder')}
                autoComplete="new-password"
                aria-invalid={!!errors.confirmPassword}
                aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                {...register('confirmPassword')}
                style={errors.confirmPassword ? inputErrorBase : inputBase}
              />
              <button
                type="button"
                aria-label={showConfirm ? t('myProfile.password.hidePassword') : t('myProfile.password.showPassword')}
                onClick={() => setShowConfirm((v) => !v)}
                style={eyeButtonStyle}
                tabIndex={0}
              >
                {showConfirm ? t('myProfile.password.hideShort') : t('myProfile.password.showShort')}
              </button>
            </div>
            {errors.confirmPassword && (
              <span
                id="confirmPassword-error"
                role="alert"
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
              >
                {errors.confirmPassword.message}
              </span>
            )}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', paddingTop: 'var(--spacing-2)' }}>
            <button
              type="submit"
              disabled={isSubmitting || mutation.isPending}
              style={{
                ...btnPrimary,
                opacity: isSubmitting || mutation.isPending ? 0.6 : 1,
                cursor: isSubmitting || mutation.isPending ? 'not-allowed' : 'pointer',
              }}
              aria-busy={mutation.isPending}
            >
              {mutation.isPending ? t('myProfile.password.submitting') : t('myProfile.password.submitButton')}
            </button>
            <button
              type="button"
              onClick={() => {
                reset();
                setServerError(undefined);
              }}
              disabled={isSubmitting || mutation.isPending}
              style={btnGhost}
            >
              {t('myProfile.password.resetButton')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
